import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function MissedWorkoutModal({ isOpen, onClose, workout, askCoach = true, onConfirm }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !workout) return null;

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await onConfirm(reason);
      setReason('');
      onClose();
    } catch (e) {
      setError(e?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50">
      <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-3 bg-white/95 backdrop-blur-md">
          <div className="min-w-0">
            <h3 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight leading-tight">
              {askCoach ? t('plan.missed_ask_coach_title') : t('plan.missed_no_change_title')}
            </h3>
            <div className="text-xs font-bold text-gray-400 mt-1 flex items-center gap-1.5">
              <span className="truncate max-w-[200px]">{workout.title}</span>
              {workout.date ? <><span>·</span><span className="font-mono">{workout.date}</span></> : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label={t('workout.close')}
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          <label className="block text-sm font-black text-gray-900 mb-2">
            {askCoach ? t('plan.missed_reason_label') : t('plan.missed_note_label')}
          </label>
          <textarea
            className="w-full border border-gray-200 rounded-xl p-4 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none text-sm transition-all h-32 resize-none"
            placeholder={askCoach ? t('plan.missed_reason_placeholder') : t('plan.missed_note_placeholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {askCoach ? (
            <div className="text-xs font-medium text-gray-500 mt-3 bg-indigo-50 p-3 rounded-lg border border-indigo-100">
              {t('plan.missed_coach_hint')}
            </div>
          ) : (
            <div className="text-xs font-medium text-gray-500 mt-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
              {t('plan.missed_no_change_hint')}
            </div>
          )}
          {loading && askCoach ? (
            <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50 p-3 flex items-center gap-2 text-xs font-bold text-indigo-800">
              <span className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full shrink-0" />
              {t('coach_options.missed_loading')}
            </div>
          ) : null}
          {error ? (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-bold text-red-700">{error}</div>
          ) : null}
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded-full transition-colors text-sm uppercase tracking-wide"
          >
            {t('workout.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className={`px-6 py-2.5 rounded-full font-bold text-white shadow-lg transition-all duration-200 text-sm uppercase tracking-wide ${
              loading 
                ? 'bg-red-400 cursor-wait shadow-none' 
                : 'bg-red-600 hover:bg-red-700 shadow-red-500/30 hover:shadow-red-500/40 hover:-translate-y-0.5'
            }`}
          >
            {loading ? (askCoach ? t('workout.analyzing') : t('plan.saving')) : (askCoach ? t('plan.ask_coach') : t('plan.missed_confirm_no_change'))}
          </button>
        </div>
      </div>
    </div>
  );
}


